/**
 * Invite people — internal staff only (spec §3.6).
 *
 * Renders `GET /api/invite` and sends through `POST /api/invitations`, which
 * forwards to `backend/` over HTTPS with the caller's own credentials. The
 * console issues no `POST` to Clerk itself.
 *
 * ## Three things the source design draws that are NOT here
 *
 *  1. **A Viewer role.** `accessScope` has no Viewer tier. An invitation sent
 *     as "Viewer" would join someone to the organization with a full member's
 *     access under a label promising less. The role list comes from the
 *     server's `INVITABLE_ROLES` and the route validates against it again —
 *     the string never reaches somewhere that might pass it through.
 *  2. **Per-project checkboxes.** There are no per-project grants: an org
 *     member sees every job site the organization owns. Drawing checkboxes
 *     over a permission model that cannot honour them would be a promise the
 *     product breaks silently. The page says so in one sentence instead.
 *  3. **The subcontractor.** The design invites `sam@baylinemech.com`. v1
 *     invites INTERNAL STAFF; partners are external and are chased with a
 *     drafted email from the item (see `NudgeSheet`). The placeholder and the
 *     copy say "your company" so the screen cannot be misread.
 *
 * ## What it does when it cannot invite
 *
 * A non-admin, a personal workspace, or a console with no backend configured
 * all render the same page with the composer visibly disabled and the reason
 * printed — never a live-looking Send that silently does nothing. The roster
 * and pending list stay readable in every one of those cases: who is on the
 * team is not an admin-only fact, and hiding it would make the page look
 * broken rather than restricted.
 */

import { useEffect, useState } from "react";
import {
  deleteJoinLinkDeactivate,
  deleteMutation,
  fetchInvite,
  fetchJoinLink,
  patchMutation,
  postJoinLinkMint,
  postMutation,
  type InviteResponse,
  type JoinLinkResource,
  type OrgInvitationRow,
  type OrgMemberRow,
  type OrgRole,
} from "../api";
import { useResource } from "../useResource";
import { C, FONT_MONO } from "../tokens";
import {
  Card,
  ConsoleViewProps,
  FailurePanel,
  LoadingPanel,
  META,
  StaleBanner,
  ViewBody,
  ViewHeader,
  initialsOf,
  plural,
  whenStamp,
} from "../components/chrome";

/** What each role actually grants, in the reader's terms. Deliberately says
 *  "every project" out loud — see the file header on the cut checkboxes. */
const ROLE_COPY: Record<OrgRole, { name: string; what: string }> = {
  admin: {
    name: "Admin",
    what: "Sets company policy, invites people, and sees every project in the organization.",
  },
  member: {
    name: "Member",
    what: "Walks, reviews and closes items. Sees every project in the organization — there are no per-project grants in this version.",
  },
};

/** A very forgiving address check. It exists to catch a typo before a round
 *  trip, not to decide validity — Clerk does that, and its answer is the one
 *  that counts. */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function Invite({ search, refreshKey, personalHref, onGoPersonal }: ConsoleViewProps) {
  const [mutations, setMutations] = useState(0);
  const [draft, setDraft] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<OrgRole>("member");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  // Team-management row state — one busy id and one error, shared across the
  // role toggle, Remove and Cancel controls. Only one of those can plausibly
  // be pressed at a time from a single reader's cursor, so there is no need
  // for a per-row copy of either.
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  // Share-a-link section state. `linkBusy` covers both mint and deactivate —
  // only one is meaningfully clickable at a time. `linkError` is one string
  // shown under whichever failed. `justCopied` is a two-second confirmation
  // for the Copy button, cleared by the effect below so it never sticks.
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [justCopied, setJustCopied] = useState(false);
  useEffect(() => {
    if (!justCopied) return;
    const id = window.setTimeout(() => setJustCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [justCopied]);

  const { data, failure, refreshFailure } = useResource<InviteResponse>(
    `invite:${search}`,
    () => fetchInvite(search),
    refreshKey + mutations,
  );
  const { data: linkResource } = useResource<JoinLinkResource>(
    `join-link:${search}`,
    () => fetchJoinLink(search),
    refreshKey + mutations,
  );

  if (failure) {
    return (
      <FailurePanel failure={failure} personalHref={personalHref} onGoPersonal={onGoPersonal} />
    );
  }
  if (!data) return <LoadingPanel shape="invite" />;

  const readOnly = !data.canInvite || !data.backendConfigured;
  // BOTH reasons when both apply — see `Policies.tsx`'s note. A personal
  // workspace has nobody to invite whether or not a backend is configured,
  // and that is the one the reader can act on.
  const readOnlyReason = [
    data.unavailableReason ?? data.readOnlyReason,
    data.backendConfigured
      ? undefined
      : "This console is not configured to reach the LedAI backend, so invitations cannot be sent from this deployment.",
  ]
    .filter(Boolean)
    .join(" ");

  const commitDraft = () => {
    const value = draft.trim();
    if (!value) return;
    if (!emails.includes(value)) setEmails((current) => [...current, value]);
    setDraft("");
  };

  /** Mint (or return the org's already-active) join link. Any member may
   *  do this; `backend`'s `handleCreateJoinLink` re-verifies membership. */
  const mintJoinLink = async () => {
    setLinkBusy(true);
    setLinkError(null);
    const result = await postJoinLinkMint(search);
    setLinkBusy(false);
    if (!result.ok) {
      setLinkError(result.error ?? `A join link could not be minted (${result.status}).`);
      return;
    }
    setMutations((count) => count + 1);
  };

  /** Copy the join-link URL to the clipboard and flash "Copied". */
  const copyJoinLink = async (url: string) => {
    setLinkError(null);
    try {
      await navigator.clipboard.writeText(url);
      setJustCopied(true);
    } catch {
      setLinkError("The console could not copy to the clipboard from this browser.");
    }
  };

  /** Revoke the current join link. The row disappears; a new one can be
   *  minted from the empty state. */
  const deactivateJoinLink = async (token: string) => {
    setLinkBusy(true);
    setLinkError(null);
    const result = await deleteJoinLinkDeactivate(search, token);
    setLinkBusy(false);
    if (!result.ok) {
      setLinkError(result.error ?? `That join link could not be revoked (${result.status}).`);
      return;
    }
    setMutations((count) => count + 1);
  };

  const send = async () => {
    const all = draft.trim() && !emails.includes(draft.trim()) ? [...emails, draft.trim()] : emails;
    if (all.length === 0) {
      setProblem("Add at least one email address first.");
      return;
    }
    const bad = all.filter((email) => !looksLikeEmail(email));
    if (bad.length > 0) {
      setProblem(`That does not look like an email address: ${bad.join(", ")}`);
      return;
    }

    setSending(true);
    setProblem(null);
    setSent(null);
    const result = await postMutation("/api/invitations", search, { emails: all, role });
    setSending(false);

    if (!result.ok) {
      setProblem(result.error ?? `The invitation was refused (${result.status}).`);
      return;
    }
    setSent(`Invited ${plural(all.length, "person", "people")} as ${ROLE_COPY[role].name}.`);
    setEmails([]);
    setDraft("");
    setMutations((count) => count + 1);
  };

  /**
   * Flip one member between Admin and Member. `data.canManage` gates whether
   * the control renders at all (see the file's `ROLE_COPY` note and
   * `getInvitePanel.ts`'s `canManage` doc) — `backend`'s
   * `handleChangeMemberRole` re-verifies the caller's own role and the
   * last-admin-demotion rule regardless, so a refusal here is a real one,
   * surfaced next to the row rather than hidden.
   */
  const changeRole = async (member: OrgMemberRow) => {
    const nextRole: OrgRole = member.role === "admin" ? "member" : "admin";
    setRowBusyId(member.userId);
    setRowError(null);
    const result = await patchMutation(`/api/members/${encodeURIComponent(member.userId)}`, search, {
      role: nextRole,
    });
    setRowBusyId(null);
    if (!result.ok) {
      setRowError(result.error ?? `That role change was refused (${result.status}).`);
      return;
    }
    setMutations((count) => count + 1);
  };

  /** Remove one member from the organization. `backend`'s `handleRemoveMember`
   *  refuses to remove the last admin regardless of what this button did. */
  const removeMember = async (member: OrgMemberRow) => {
    setRowBusyId(member.userId);
    setRowError(null);
    const result = await deleteMutation(`/api/members/${encodeURIComponent(member.userId)}`, search);
    setRowBusyId(null);
    if (!result.ok) {
      setRowError(result.error ?? `That member could not be removed (${result.status}).`);
      return;
    }
    setMutations((count) => count + 1);
  };

  /** Cancel one pending invitation. */
  const cancelInvitation = async (invitation: OrgInvitationRow) => {
    setRowBusyId(invitation.id);
    setRowError(null);
    const result = await deleteMutation(`/api/invitations/${encodeURIComponent(invitation.id)}`, search);
    setRowBusyId(null);
    if (!result.ok) {
      setRowError(result.error ?? `That invitation could not be canceled (${result.status}).`);
      return;
    }
    setMutations((count) => count + 1);
  };

  return (
    <>
      <ViewHeader
        crumb={`${plural(data.members.length, "member", "members")} · ${plural(
          data.pending.length,
          "pending",
          "pending",
        )}`}
        title="Invite people"
        note="Internal staff only"
      />

      <ViewBody>
        {refreshFailure && <StaleBanner failure={refreshFailure} />}

        {readOnlyReason.length > 0 && (
          <div
            data-testid="invite-readonly-reason"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "12px 14px",
              borderRadius: 12,
              background: C.amberSoft,
              border: `1px solid ${C.line}`,
              marginBottom: 16,
              fontSize: 14.37,
              color: C.ink,
              lineHeight: 1.5,
            }}
          >
            <span
              style={{ width: 6, height: 6, borderRadius: 3, background: C.amber, flexShrink: 0, marginTop: 6 }}
            />
            {readOnlyReason}
          </div>
        )}

        {/* ---------- Share a link ----------
         *
         * Any org MEMBER may mint / read / deactivate this — same rule the
         * email composer uses. Hidden entirely when the caller is not a
         * member (`canInvite: false`) or the console is not configured to
         * reach the backend: there is no join link to share for an org you
         * are not in, and no server to send a mint to. Failure lands under
         * the section as `linkError`, never in place of the whole view.
         *
         * The section itself renders once `linkResource` is loaded — a
         * flash of an empty "Generate" state before the fetch resolves
         * would look, for a fraction of a second, as if the org had never
         * had a link. */}
        {!readOnly && linkResource && (
          <Card
            testId="join-link-section"
            style={{ marginBottom: 18 }}
          >
            <div style={{ ...META, fontSize: 10.93, letterSpacing: "1.2px", color: C.ink, marginBottom: 9 }}>
              Share a link
            </div>

            {linkResource.unavailable ? (
              <div
                data-testid="join-link-unavailable"
                style={{ fontSize: 14.37, color: C.sub, marginBottom: 4 }}
              >
                {linkResource.unavailable}
              </div>
            ) : linkResource.link === null ? (
              <>
                <div
                  style={{
                    fontSize: 14.37,
                    color: C.sub,
                    marginBottom: 12,
                    lineHeight: 1.5,
                  }}
                >
                  A shareable link joins anyone who opens it to this
                  organization as a member. Anyone with the link can use
                  it until you revoke it, so share it with people you know.
                </div>
                <button
                  data-testid="join-link-generate"
                  disabled={linkBusy}
                  onClick={mintJoinLink}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    background: linkBusy ? C.faint : C.gold,
                    color: linkBusy ? C.sub : C.ink,
                    border: `1.5px solid ${C.line}`,
                    fontWeight: 700,
                    fontSize: 14.37,
                    cursor: linkBusy ? "wait" : "pointer",
                  }}
                >
                  {linkBusy ? "Generating…" : "Generate a shareable link"}
                </button>
              </>
            ) : (
              (() => {
                const link = linkResource.link;
                const url =
                  typeof window !== "undefined"
                    ? `${window.location.origin}/join/${link.token}`
                    : `/join/${link.token}`;
                const daysUntil = Math.max(
                  0,
                  Math.ceil(
                    (new Date(link.expiresAt).getTime() - Date.now()) /
                      86_400_000,
                  ),
                );
                return (
                  <>
                    <div
                      data-testid="join-link-url"
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 13.4,
                        padding: "10px 12px",
                        borderRadius: 10,
                        background: C.faint,
                        border: `1px solid ${C.line}`,
                        wordBreak: "break-all",
                        marginBottom: 8,
                      }}
                    >
                      {url}
                    </div>
                    <div
                      style={{
                        fontSize: 12.8,
                        color: C.sub,
                        marginBottom: 12,
                      }}
                    >
                      Expires in {plural(daysUntil, "day", "days")}. Anyone
                      with the link can join as a member until then, or
                      until you revoke it.
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        data-testid="join-link-copy"
                        disabled={linkBusy}
                        onClick={() => copyJoinLink(url)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: 10,
                          background: justCopied ? C.gold : C.surface,
                          color: C.ink,
                          border: `1.5px solid ${justCopied ? C.gold : C.line}`,
                          fontWeight: 600,
                          fontSize: 13.4,
                          cursor: linkBusy ? "wait" : "pointer",
                        }}
                      >
                        {justCopied ? "Copied" : "Copy link"}
                      </button>
                      <button
                        data-testid="join-link-deactivate"
                        disabled={linkBusy}
                        onClick={() => deactivateJoinLink(link.token)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: 10,
                          background: C.surface,
                          color: C.ink,
                          border: `1.5px solid ${C.line}`,
                          fontWeight: 600,
                          fontSize: 13.4,
                          cursor: linkBusy ? "wait" : "pointer",
                        }}
                      >
                        {linkBusy ? "Revoking…" : "Revoke link"}
                      </button>
                    </div>
                  </>
                );
              })()
            )}

            {linkError && (
              <div
                data-testid="join-link-error"
                style={{
                  marginTop: 10,
                  padding: "8px 12px",
                  borderRadius: 10,
                  background: C.amberSoft,
                  border: `1px solid ${C.line}`,
                  fontSize: 13.4,
                  color: C.ink,
                }}
              >
                {linkError}
              </div>
            )}
          </Card>
        )}

        {/* ---------- composer ---------- */}
        <Card style={{ marginBottom: 18 }}>
          <div style={{ ...META, fontSize: 10.93, letterSpacing: "1.2px", color: C.ink, marginBottom: 9 }}>
            Invite by email
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 7,
              alignItems: "center",
              background: C.faint,
              borderRadius: 12,
              padding: "9px 11px",
              marginBottom: 14,
              opacity: readOnly ? 0.55 : 1,
            }}
          >
            {emails.map((email) => (
              <span
                key={email}
                data-testid="invite-chip"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: C.surface,
                  border: `1px solid ${C.line}`,
                  borderRadius: 999,
                  padding: "5px 7px 5px 11px",
                  fontSize: 13.8,
                  fontWeight: 600,
                }}
              >
                {email}
                <button
                  aria-label={`Remove ${email}`}
                  disabled={readOnly}
                  onClick={() => setEmails((current) => current.filter((value) => value !== email))}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 8,
                    background: C.faint,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
                    <path d="M5 5l14 14M19 5L5 19" stroke={C.sub} strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ))}
            <input
              data-testid="invite-email"
              type="email"
              disabled={readOnly}
              data-readonly={readOnly ? "true" : "false"}
              value={draft}
              // `onInput`, not `onChange`. They are the same event for a text
              // input in React, and this one is the one that actually fires
              // under the console's own test DOM (React 19 + happy-dom never
              // synthesises the change event for a text input, so an
              // `onChange` handler here would be untestable — and an
              // untestable composer is how a typed address silently stops
              // reaching the send button).
              onInput={(event) => setDraft((event.target as HTMLInputElement).value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === ",") {
                  event.preventDefault();
                  commitDraft();
                }
              }}
              onBlur={commitDraft}
              placeholder="name@yourcompany.com"
              style={{
                flex: 1,
                minWidth: 200,
                border: 0,
                outline: "none",
                background: "transparent",
                fontSize: 14.37,
                color: C.ink,
                padding: "5px 4px",
              }}
            />
          </div>

          <div style={{ ...META, fontSize: 10.93, letterSpacing: "1.2px", color: C.ink, marginBottom: 9 }}>
            Role
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {data.roles.map((option) => {
              const active = option === role;
              const copy = ROLE_COPY[option];
              return (
                <button
                  key={option}
                  data-testid="invite-role"
                  data-role={option}
                  aria-pressed={active}
                  disabled={readOnly}
                  data-readonly={readOnly ? "true" : "false"}
                  onClick={() => setRole(option)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: 13,
                    background: active ? C.surface : "transparent",
                    border: `1.5px solid ${active ? C.gold : C.line}`,
                    textAlign: "left",
                    opacity: readOnly ? 0.6 : 1,
                    cursor: readOnly ? "not-allowed" : "pointer",
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 9,
                      border: `2px solid ${active ? C.gold : C.line2}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 4,
                        background: active ? C.gold : "transparent",
                      }}
                    />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 16.1, fontWeight: 700, letterSpacing: "-0.2px" }}>
                      {copy.name}
                    </span>
                    <span style={{ display: "block", fontSize: 14.37, color: C.sub, marginTop: 4, lineHeight: 1.45 }}>
                      {copy.what}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {problem && (
            <div
              data-testid="invite-error"
              style={{ fontSize: 14.37, color: C.red, marginBottom: 12, lineHeight: 1.45 }}
            >
              {problem}
            </div>
          )}
          {sent && (
            <div
              data-testid="invite-sent"
              style={{ fontSize: 14.37, color: C.green, marginBottom: 12, lineHeight: 1.45 }}
            >
              {sent}
            </div>
          )}

          <button
            data-testid="invite-send"
            disabled={readOnly || sending}
            data-readonly={readOnly ? "true" : "false"}
            onClick={send}
            style={{
              height: 44,
              width: "100%",
              borderRadius: 12,
              background: readOnly ? C.faint : C.gold,
              color: readOnly ? C.sub : C.onGold,
              fontSize: 16.1,
              fontWeight: 700,
              opacity: readOnly ? 0.7 : 1,
              cursor: readOnly ? "not-allowed" : "pointer",
            }}
          >
            {sending
              ? "Sending…"
              : `Send ${plural(emails.length || 1, "invite", "invites")} as ${ROLE_COPY[role].name}`}
          </button>

          <div
            data-testid="invite-scope-note"
            style={{ fontSize: 13.8, color: C.sub2, marginTop: 10, lineHeight: 1.5 }}
          >
            Everyone you invite here joins your organization and can see every project it owns.
            There are no per-project invitations in this version, and there is no read-only tier —
            subcontractors are not LedAI users, and are chased with a message you send them from
            the item.
          </div>
        </Card>

        {rowError && (
          <div
            data-testid="team-row-error"
            style={{ fontSize: 14.37, color: C.red, marginBottom: 12, lineHeight: 1.45 }}
          >
            {rowError}
          </div>
        )}

        {/* ---------- pending ---------- */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ ...META, fontSize: 10.93, letterSpacing: "1.2px", color: C.ink }}>
              Pending
            </span>
            <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: C.sub2 }}>
              {data.pending.length}
            </span>
            <span style={{ flex: 1, height: 1, background: C.line }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {data.pendingUnavailableReason && (
              <div data-testid="pending-unavailable" style={{ fontSize: 14.37, color: C.sub }}>
                {data.pendingUnavailableReason}
              </div>
            )}
            {!data.pendingUnavailableReason && data.pending.length === 0 && (
              <div data-testid="pending-empty" style={{ fontSize: 14.37, color: C.sub }}>
                Nobody has an unanswered invitation.
              </div>
            )}
            {data.pending.map((invitation) => (
              <div
                key={invitation.id}
                data-testid="pending-invite"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  padding: "11px 13px",
                  background: C.canvas,
                  border: `1px dashed ${C.line2}`,
                  borderRadius: 13,
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 17,
                    background: C.amberSoft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={C.amber}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m22 6-10 7L2 6" />
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                  </svg>
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14.95, fontWeight: 500, color: C.sub }}>
                    {invitation.email}
                  </span>
                  <span
                    style={{ ...META, display: "block", fontSize: 10.35, color: C.amber, marginTop: 3, letterSpacing: "0.3px" }}
                  >
                    {`Invited ${whenStamp(invitation.createdAt)} · ${invitation.role}`}
                  </span>
                </span>
                {data.canManage && (
                  <button
                    data-testid="invitation-cancel"
                    disabled={rowBusyId === invitation.id}
                    onClick={() => cancelInvitation(invitation)}
                    style={{
                      flexShrink: 0,
                      padding: "6px 11px",
                      borderRadius: 9,
                      background: "transparent",
                      border: `1px solid ${C.line2}`,
                      fontSize: 13.23,
                      fontWeight: 600,
                      color: C.sub,
                      cursor: rowBusyId === invitation.id ? "not-allowed" : "pointer",
                      opacity: rowBusyId === invitation.id ? 0.6 : 1,
                    }}
                  >
                    {rowBusyId === invitation.id ? "Canceling…" : "Cancel"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ---------- current members ---------- */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ ...META, fontSize: 10.93, letterSpacing: "1.2px", color: C.ink }}>
              Already in
            </span>
            <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: C.sub2 }}>
              {data.members.length}
            </span>
            <span style={{ flex: 1, height: 1, background: C.line }} />
          </div>
          <Card style={{ padding: 0, overflow: "hidden", borderRadius: 14 }}>
            {data.membersUnavailableReason && (
              <div
                data-testid="members-unavailable"
                style={{ padding: 16, fontSize: 14.37, color: C.sub, lineHeight: 1.5 }}
              >
                {data.membersUnavailableReason}
              </div>
            )}
            {!data.membersUnavailableReason && data.members.length === 0 && (
              <div style={{ padding: 16, fontSize: 14.37, color: C.sub }}>
                Nobody else is in this organization yet.
              </div>
            )}
            {data.members.map((member, index) => (
              <div
                key={member.userId}
                data-testid="org-member"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 15px",
                  borderTop: index === 0 ? "1px solid transparent" : `1px solid ${C.line}`,
                }}
              >
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 15,
                    background: C.ink,
                    color: C.surface,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12.65,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {initialsOf(member.name)}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 15.52, fontWeight: 700, letterSpacing: "-0.2px" }}>
                    {member.name}
                  </span>
                  {member.email && (
                    <span
                      style={{ ...META, display: "block", fontSize: 10.35, color: C.sub2, marginTop: 2, letterSpacing: "0.4px" }}
                    >
                      {member.email}
                    </span>
                  )}
                </span>
                <span style={{ ...META, fontSize: 9.77, color: C.sub, letterSpacing: "0.7px" }}>
                  {member.role}
                </span>
                {data.canManage && (
                  <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button
                      data-testid="member-role-toggle"
                      data-role={member.role}
                      disabled={rowBusyId === member.userId}
                      onClick={() => changeRole(member)}
                      style={{
                        padding: "6px 11px",
                        borderRadius: 9,
                        background: "transparent",
                        border: `1px solid ${C.line2}`,
                        fontSize: 13.23,
                        fontWeight: 600,
                        color: C.sub,
                        cursor: rowBusyId === member.userId ? "not-allowed" : "pointer",
                        opacity: rowBusyId === member.userId ? 0.6 : 1,
                      }}
                    >
                      {rowBusyId === member.userId
                        ? "…"
                        : `Make ${member.role === "admin" ? "member" : "admin"}`}
                    </button>
                    <button
                      data-testid="member-remove"
                      disabled={rowBusyId === member.userId}
                      onClick={() => removeMember(member)}
                      style={{
                        padding: "6px 11px",
                        borderRadius: 9,
                        background: "transparent",
                        border: `1px solid ${C.line2}`,
                        fontSize: 13.23,
                        fontWeight: 600,
                        color: C.red,
                        cursor: rowBusyId === member.userId ? "not-allowed" : "pointer",
                        opacity: rowBusyId === member.userId ? 0.6 : 1,
                      }}
                    >
                      Remove
                    </button>
                  </span>
                )}
              </div>
            ))}
          </Card>
        </div>
      </ViewBody>
    </>
  );
}
